/**
 * Mutation Queue — reliable data persistence layer.
 *
 * Replaces scattered setTimeout saves with a centralized queue that:
 * 1. Accepts mutations (item ID + updates)
 * 2. Debounces rapid updates (coalesces per-item mutations)
 * 3. Persists pending mutations to IndexedDB (crash protection)
 * 4. Flushes to Supabase in batches
 * 5. Flushes on beforeunload/visibilitychange for tab-close safety
 */

import { supabase } from '@/integrations/supabase/client';

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

const DB_NAME = 'mindfull_mutations';
const DB_VERSION = 1;
const STORE_NAME = 'pending';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(key: string, value: unknown): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put({ key, value, ts: Date.now() });
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch (e) {
    console.warn('[MutationQueue] IndexedDB put failed:', e);
  }
}

async function idbDelete(key: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(key);
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch (e) {
    console.warn('[MutationQueue] IndexedDB delete failed:', e);
  }
}

async function idbGetAll(): Promise<Array<{ key: string; value: { id: string; updates: Record<string, unknown> }; ts: number }>> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => { db.close(); resolve(req.result || []); };
      req.onerror = () => { db.close(); reject(req.error); };
    });
  } catch (e) {
    console.warn('[MutationQueue] IndexedDB getAll failed:', e);
    return [];
  }
}

async function idbClear(): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch (e) {
    console.warn('[MutationQueue] IndexedDB clear failed:', e);
  }
}

// ---------------------------------------------------------------------------
// Mutation Queue
// ---------------------------------------------------------------------------

interface PendingMutation {
  id: string;
  updates: Record<string, unknown>;
}

/** In-memory queue of pending mutations (coalesced by item ID + category) */
const pendingMutations = new Map<string, PendingMutation>();

/** Flush timer */
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/** Flush delay (ms) — how long to debounce before flushing */
const FLUSH_DELAY = 500;

/** Whether a flush is currently in progress */
let isFlushing = false;

/** Callbacks for state updates (isSaving flag) */
let onSavingStart: (() => void) | null = null;
let onSavingEnd: (() => void) | null = null;
/** Callback for content change (re-generate embeddings) */
let onContentChanged: ((itemId: string) => void) | null = null;

/**
 * Configure the queue's callbacks.
 * Call once at app startup.
 */
export function configureMutationQueue(opts: {
  onSavingStart?: () => void;
  onSavingEnd?: () => void;
  onContentChanged?: (itemId: string) => void;
}): void {
  onSavingStart = opts.onSavingStart ?? null;
  onSavingEnd = opts.onSavingEnd ?? null;
  onContentChanged = opts.onContentChanged ?? null;
}

/**
 * Enqueue a mutation for an item.
 * Coalesces updates: if there's already a pending mutation for this item+category,
 * the updates are merged (latest wins).
 *
 * @param id - Item ID
 * @param updates - Partial fields to update
 * @param category - 'position' or 'content' — separate debounce tracks
 */
export function enqueueMutation(
  id: string,
  updates: Record<string, unknown>,
  category: 'position' | 'content' = 'content',
): void {
  const key = `${category}:${id}`;
  const existing = pendingMutations.get(key);

  if (existing) {
    // Merge updates (latest values win)
    existing.updates = { ...existing.updates, ...updates };
  } else {
    pendingMutations.set(key, { id, updates: { ...updates } });
  }

  // Persist to IndexedDB for crash protection
  idbPut(key, { id, updates: pendingMutations.get(key)!.updates }).catch(() => {});

  // Schedule flush
  scheduleFlush();
}

/** Schedule a flush (debounced) */
function scheduleFlush(): void {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => flush(), FLUSH_DELAY);
}

/**
 * Flush all pending mutations to Supabase.
 * Batches writes by item ID and sends them in parallel.
 */
export async function flush(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  if (pendingMutations.size === 0) return;
  if (isFlushing) {
    // Another flush is in progress — reschedule
    scheduleFlush();
    return;
  }

  isFlushing = true;
  onSavingStart?.();

  // Snapshot and clear the queue
  const batch = new Map(pendingMutations);
  pendingMutations.clear();

  // Coalesce by item ID (merge position + content updates for same item)
  const byItem = new Map<string, Record<string, unknown>>();
  const contentChangedIds: string[] = [];

  for (const [key, mutation] of batch) {
    const existing = byItem.get(mutation.id);
    if (existing) {
      Object.assign(existing, mutation.updates);
    } else {
      byItem.set(mutation.id, { ...mutation.updates });
    }
    if (key.startsWith('content:') && mutation.updates.content) {
      contentChangedIds.push(mutation.id);
    }
  }

  // Execute batch writes
  const writes = Array.from(byItem.entries()).map(async ([id, updates]) => {
    const { error } = await supabase
      .from('items')
      .update(updates)
      .eq('id', id);

    if (error) {
      console.error('[MutationQueue] Save failed for', id, error);
      // Re-enqueue failed mutation for retry
      const key = `retry:${id}`;
      pendingMutations.set(key, { id, updates });
      return;
    }

    // Clean up IndexedDB entries for this item
    for (const batchKey of batch.keys()) {
      if (batchKey.endsWith(`:${id}`)) {
        idbDelete(batchKey).catch(() => {});
      }
    }
  });

  await Promise.all(writes);

  // Trigger embedding regeneration for content changes
  for (const itemId of contentChangedIds) {
    onContentChanged?.(itemId);
  }

  isFlushing = false;

  // If new mutations arrived during flush, schedule another
  if (pendingMutations.size > 0) {
    scheduleFlush();
  } else {
    onSavingEnd?.();
  }
}

/**
 * Recover any mutations that were persisted to IndexedDB but not flushed
 * (e.g., after a crash or forced tab close). Call once at app startup.
 */
export async function recoverPendingMutations(): Promise<number> {
  const stored = await idbGetAll();
  if (stored.length === 0) return 0;

  console.log(`[MutationQueue] Recovering ${stored.length} pending mutations from IndexedDB`);

  for (const entry of stored) {
    const { id, updates } = entry.value;
    pendingMutations.set(entry.key, { id, updates });
  }

  // Flush recovered mutations
  await flush();
  return stored.length;
}

/**
 * Register browser event listeners for flush triggers.
 * Call once at app startup (e.g., in main.tsx).
 */
export function registerFlushListeners(): void {
  if (typeof window === 'undefined') return;

  // Flush on tab close/refresh
  window.addEventListener('beforeunload', () => {
    if (pendingMutations.size === 0) return;

    // Synchronous flush attempt: use navigator.sendBeacon isn't practical with auth,
    // so we do a best-effort async flush. The browser may allow it to complete for
    // short operations. IndexedDB persistence serves as backup.
    flush();
  });

  // Flush when tab becomes hidden (user switches tabs)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && pendingMutations.size > 0) {
      flush();
    }
  });
}

/** Get the number of pending mutations (for UI indicators) */
export function getPendingCount(): number {
  return pendingMutations.size;
}
